/**
 * Decisiones de resume que el provider sigue ejecutando después del interceptor
 * intake y del mapa `resolveResumeRoute`: destino post-firma (status SIGNED, por país) y
 * redirecciones por `step` del quote_form_summary.
 *
 * Puros a propósito: el contexto hace los fetches y navega; acá solo vive la decisión.
 */

import {
  isSignatureCeremonyBlockedStatus,
  isSignatureRetryEligible,
  shouldAutoStartDigitalSignature,
} from '../../utils/signatureAttempt';

/**
 * Pantalla "solicitud enviada": identifica al postulante por email, no por token.
 * /congratulations (no /mx/congratulations): la ruta con prefijo se removió en nov-2025
 * y App.tsx + el webhook de Logalty usan esta.
 */
function congratulationsTarget(email?: string | null): string {
  return email ? `/congratulations?email=${encodeURIComponent(email)}` : '/congratulations';
}

/** Destino de pago: la sesión del summary o el fallback global. */
export function resolvePaymentTarget(
  paymentSession: string | null | undefined,
  fallbackUrl: string,
): { target: string; reason: string } {
  if (paymentSession) {
    return { target: paymentSession, reason: 'payment_session' };
  }
  return { target: fallbackUrl, reason: 'payment_web_fallback' };
}

export type SignedTargetInput = {
  country: string;
  paymentSession?: string | null;
  fallbackUrl: string;
  email?: string | null;
};

/**
 * Destino del resume para el status SIGNED, que depende del país.
 *
 * En MX la firma no crea sesión de pago —el pago se arma al aprobar— y el webhook de firma
 * ya redirige a /congratulations: mirar payment_session mandaba a TODO cliente MX firmado al
 * fallback global, que apunta al sitio de pagos de Brasil. Fuera de MX (BR paga al firmar) el
 * destino sigue siendo la sesión de pago, o el fallback si no vino.
 */
export function resolveSignedTarget(input: SignedTargetInput): { target: string; reason: string } {
  if (input.country === 'MX') {
    return { target: congratulationsTarget(input.email), reason: 'signed_mx_congratulations' };
  }

  return resolvePaymentTarget(input.paymentSession, input.fallbackUrl);
}

export type SummaryStepInput = {
  step?: string | null;
  country: string;
  email?: string | null;
  /** Application status, when the resume already loaded it. */
  applicationStatus?: string | null;
  /** `error` query from the Logalty return URL. */
  signatureError?: string | null;
  /** User clicked "Reintentar firma". Never inferred from a page load. */
  explicitRetry?: boolean;
};

export type SummaryStepDecision =
  | { action: 'stay'; reason: string }
  | { action: 'digital_signature'; reason: string }
  | { action: 'signature_retry'; reason: string }
  | { action: 'redirect'; target: string; reason: string };

/** Matriz del `step` del summary (segunda fuente de estado del resume). */
export function resolveSummaryStepTarget(input: SummaryStepInput): SummaryStepDecision {
  const isMX = input.country === 'MX';

  if (input.step === 'digital_signature') {
    // Signed, paid or already in evaluation must not be pulled back into a
    // ceremony, whether or not the URL still has retry=1. Treating that as
    // "hold" would show the cancel screen instead of the status page.
    if (isSignatureCeremonyBlockedStatus(input.applicationStatus)) {
      return { action: 'stay', reason: 'signature_not_available_for_status' };
    }
    const honorRetry = Boolean(input.explicitRetry) && isSignatureRetryEligible(input.applicationStatus);
    if (
      !shouldAutoStartDigitalSignature({
        applicationStatus: input.applicationStatus,
        signatureError: input.signatureError,
        explicitRetry: honorRetry,
      })
    ) {
      return { action: 'signature_retry', reason: 'signature_cancelled_hold' };
    }
    return {
      action: 'digital_signature',
      reason: honorRetry ? 'explicit_signature_retry' : 'step_digital_signature',
    };
  }

  if (input.step === 'signed' && isMX) {
    return {
      action: 'redirect',
      target: congratulationsTarget(input.email),
      reason: 'step_signed_mx',
    };
  }

  if (input.step === 'rejected by client' && isMX) {
    return { action: 'redirect', target: '/rejection-reminder', reason: 'step_rejected_by_client_mx' };
  }

  return { action: 'stay', reason: 'step_no_redirect' };
}
